import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import ChatInput from '../../pages/events-explorer/components/AIHelperPanel/ChatInput';
import MarkdownRenderer from '../MarkdownRenderer';
import { VOICE_LANGUAGES } from '../../pages/events-explorer/services/openaiVoiceService';

// --- Animations ---

const _kfAccent = localStorage.getItem('theme') === 'light' ? '35, 99, 247' : '191, 232, 45';

const pulseVibrate = keyframes`
  0%, 100% { transform: scale(1) translateX(0); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3); }
  25% { transform: scale(1.08) translateX(-2px); box-shadow: 0 4px 20px rgba(${_kfAccent}, 0.5); }
  50% { transform: scale(1.08) translateX(2px); box-shadow: 0 4px 20px rgba(${_kfAccent}, 0.5); }
  75% { transform: scale(1.08) translateX(-2px); box-shadow: 0 4px 20px rgba(${_kfAccent}, 0.5); }
`;

const igniteGlow = keyframes`
  0%, 100% { box-shadow: 0 0 5px rgba(${_kfAccent}, 0.3), inset 0 0 5px rgba(${_kfAccent}, 0.1); }
  50% { box-shadow: 0 0 20px rgba(${_kfAccent}, 0.6), inset 0 0 10px rgba(${_kfAccent}, 0.2); }
`;

const pulseDot = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const dot = keyframes`
  0%, 80%, 100% { transform: scale(0); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
`;

// --- Styled Components ---

const PanelContainer = styled.div`
  position: fixed;
  right: ${({ $isOpen }) => ($isOpen ? '0' : '-400px')};
  top: 0;
  width: 400px;
  max-width: 100vw;
  height: 100vh;
  height: 100dvh;
  background: ${({ theme }) => theme.black};
  border-left: 1px solid ${({ theme }) => theme.borderColor};
  display: flex;
  flex-direction: column;
  transition: right 0.3s ease-in-out;
  z-index: 1000;
  box-shadow: ${({ $isOpen }) => ($isOpen ? '-4px 0 20px rgba(0, 0, 0, 0.3)' : 'none')};
  font-family: 'SatoshiRegular', sans-serif;

  @media (max-width: 450px) {
    width: 100vw;
    right: ${({ $isOpen }) => ($isOpen ? '0' : '-100vw')};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};
  background: ${({ theme }) => theme.lines};
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HeaderIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  svg { width: 100%; height: 100%; }
  path { fill: ${({ theme }) => theme.green}; }
`;

const Title = styled.h3`
  font-size: 16px;
  font-family: 'SatoshiBold';
  color: ${({ theme }) => theme.blueish};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const HeaderTutorialButton = styled.button`
  min-height: 30px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.borderColor};
  background: ${({ theme }) => theme.cardBgAlt};
  color: ${({ theme }) => theme.green};
  padding: 0 12px;
  font-size: 12px;
  font-family: 'SatoshiMedium', 'SatoshiRegular', sans-serif;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.green};
    background: ${({ theme }) => theme.green};
    color: ${({ theme }) => theme.textOnAccent};
  }
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  min-width: 36px;
  border: none;
  border-radius: 6px;
  background: ${({ theme }) => theme.black};
  color: ${({ theme }) => theme.blueish2};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-family: 'SatoshiRegular';
  transition: all 0.2s;
  flex-shrink: 0;
  &:hover { background: ${({ theme }) => theme.borderColor}; color: ${({ theme }) => theme.blueish}; }
  &:active { transform: scale(0.95); }
`;


const SettingsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.blueish2};
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  svg { width: 16px; height: 16px; fill: currentColor; }
  &:hover { border-color: ${({ theme }) => theme.green}; color: ${({ theme }) => theme.green}; }
  &:active { transform: scale(0.95); }
`;

const SettingsPanel = styled.div`
  overflow: hidden;
  max-height: ${({ $open }) => $open ? '300px' : '0'};
  transition: max-height 0.25s ease-in-out;
  border-bottom: ${({ $open, theme }) => $open ? `1px solid ${theme.borderColor}` : 'none'};
  background: ${({ theme }) => theme.lines};
`;

const SettingsPanelInner = styled.div`
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SettingsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const SettingsLabel = styled.span`
  font-size: 13px;
  font-family: 'SatoshiRegular';
  color: ${({ theme }) => theme.blueish};
  white-space: nowrap;
`;

const SwitchLabel = styled.label`
  position: relative;
  display: inline-block;
  width: 42px;
  height: 24px;
  flex-shrink: 0;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
`;

const SwitchSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.blueish3};
  transition: 0.25s;
  border-radius: 34px;

  &:before {
    position: absolute;
    content: "";
    height: 20px;
    width: 20px;
    left: 2px;
    bottom: 2px;
    background-color: #fff;
    transition: 0.25s;
    border-radius: 50%;
  }

  input:checked + & {
    background-color: ${({ theme }) => theme.green};
  }

  input:checked + &:before {
    transform: translateX(18px);
  }
`;

const LanguageFilterInput = styled.input`
  width: 100%;
  height: 30px;
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.blueish3};
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 13px;
  font-family: 'SatoshiRegular';
  background: transparent;
  color: ${({ theme }) => theme.white};
  outline: none;

  &::placeholder {
    color: ${({ theme }) => theme.blueish2};
  }

  &:focus {
    border-color: ${({ theme }) => theme.green};
  }
`;

const LanguageList = styled.div`
  max-height: 160px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const LanguageItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 6px 8px;
  border: none;
  background: ${({ $selected, theme }) => $selected ? theme.black : 'transparent'};
  color: ${({ $selected, theme }) => $selected ? theme.green : theme.blueish};
  font-size: 13px;
  font-family: ${({ $selected }) => $selected ? 'SatoshiBold' : 'SatoshiRegular'};
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;
  border-radius: 4px;

  &:hover {
    background: ${({ theme }) => theme.black};
  }
`;

const LanguageCode = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.blueish2};
  font-family: 'SatoshiRegular';
`;

const ResetButton = styled.button`
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.blueish2};
  cursor: pointer;
  font-size: 12px;
  font-family: 'SatoshiRegular';
  transition: all 0.2s;
  white-space: nowrap;
  &:hover { border-color: ${({ theme }) => theme.blueish}; color: ${({ theme }) => theme.blueish}; }
  &:active { transform: scale(0.95); }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-family: 'SatoshiRegular';
`;

const WelcomeMessage = styled.div`
  text-align: center;
  padding: 24px 16px;
  color: ${({ theme }) => theme.blueish2};
  font-family: 'SatoshiRegular';
  font-size: 14px;
  line-height: 1.6;
`;

const WelcomeTitle = styled.div`
  font-family: 'SatoshiBold';
  font-size: 18px;
  color: ${({ theme }) => theme.blueish};
  margin-bottom: 12px;
`;

const ExamplePrompts = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ExamplePrompt = styled.button`
  padding: 10px 14px;
  background: ${({ theme }) => theme.lines};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 8px;
  color: ${({ theme }) => theme.blueish};
  font-family: 'SatoshiRegular';
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: ${({ theme }) => theme.green}; background: ${({ theme }) => theme.black}; }
`;

const ToggleButton = styled.button`
  position: fixed;
  right: 20px;
  bottom: 20px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${({ theme }) => theme.green};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: all 0.2s;
  z-index: 999;
  &:hover { transform: scale(1.05); box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4); }
  ${({ $isRecording }) => $isRecording && css`
    animation: ${pulseVibrate} 0.6s ease-in-out infinite;
    &:hover { transform: none; }
  `}
`;

const SparklesIcon = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  svg { width: 100%; height: 100%; }
  path { fill: ${({ theme }) => theme.black}; }
`;

const InputContainer = styled.div`
  padding: 16px;
  border-top: 1px solid ${({ theme }) => theme.borderColor};
  background: ${({ theme }) => theme.lines};
  ${({ $isRecording }) => $isRecording && css`
    animation: ${igniteGlow} 1.2s ease-in-out infinite;
  `}
`;

const RecordingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 0;
  color: ${({ theme }) => theme.blueish};
  font-family: 'SatoshiRegular';
  font-size: 14px;
  &::before {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #ef4444;
    animation: ${pulseDot} 1s ease-in-out infinite;
    flex-shrink: 0;
  }
`;

// --- Message Components ---

const MessageBubbleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  animation: ${fadeIn} 0.3s ease-out;
`;

const MessageBubble = styled.div`
  max-width: 85%;
  padding: 12px 16px;
  border-radius: ${({ $isUser }) => ($isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px')};
  background: ${({ $isUser, theme }) => ($isUser ? theme.green : theme.lines)};
  color: ${({ $isUser }) => ($isUser ? '#1a1a1a' : 'inherit')};
  font-family: 'SatoshiRegular';
  font-size: 14px;
  line-height: 1.5;
  word-wrap: break-word;
`;

const MessageContent = styled.div`
  font-family: SatoshiRegular, sans-serif;
  color: inherit;
  p, ul, ol, li, span { font-family: SatoshiRegular, sans-serif; }
  p { margin: 0 0 8px 0; &:last-child { margin-bottom: 0; } }
  strong { font-family: SatoshiBold, sans-serif; font-weight: 700; }
  ul, ol { margin: 8px 0; padding-left: 20px; }
  li { margin: 4px 0; }
  a { color: ${({ theme, $isUser }) => $isUser ? '#1a1a1a' : theme.green}; text-decoration: underline; }
  h1, h2, h3, h4, h5, h6 { font-family: SatoshiBold, sans-serif; margin: 12px 0 8px 0; &:first-child { margin-top: 0; } }
  h1 { font-size: 18px; } h2 { font-size: 16px; } h3 { font-size: 15px; }
`;

const LoadingDots = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px 0;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  background: ${({ theme }) => theme.blueish2};
  border-radius: 50%;
  animation: ${dot} 1.4s infinite ease-in-out both;
  &:nth-child(1) { animation-delay: -0.32s; }
  &:nth-child(2) { animation-delay: -0.16s; }
`;

const StatusText = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.blueish2};
  font-family: 'SatoshiRegular';
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ErrorMsg = styled.div`
  color: #ff6b6b;
  font-size: 13px;
  padding: 8px 12px;
  background: rgba(255, 107, 107, 0.1);
  border-radius: 8px;
  margin-top: 8px;
  font-family: 'SatoshiRegular';
`;

const GLOBAL_AI_TUTORIAL_STORAGE_KEY = 'global_ai_tutorial_seen_v1';
const _isLightTheme = localStorage.getItem('theme') === 'light';
const GLOBAL_AI_TUTORIAL_ACCENT = _isLightTheme ? '#2363F7' : '#BEFF00';
const GLOBAL_AI_TUTORIAL_CARD_BG = _isLightTheme
  ? '#ffffff'
  : 'linear-gradient(145deg, #1a2612, #131c0e)';
const GLOBAL_AI_TUTORIAL_TITLE_COLOR = _isLightTheme ? '#0f1117' : '#ffffff';
const GLOBAL_AI_TUTORIAL_BODY_COLOR = _isLightTheme ? '#6B7280' : '#8a9e80';
const GLOBAL_AI_TUTORIAL_SKIP_COLOR = _isLightTheme ? '#9CA3AF' : '#5a6e50';
const GLOBAL_AI_TUTORIAL_BTN_TEXT = _isLightTheme ? '#ffffff' : '#0a0e05';
const GLOBAL_AI_TUTORIAL_STEP_INACTIVE = _isLightTheme ? '#E5E7EB' : '#ffffff22';
const GLOBAL_AI_TUTORIAL_FONT_BASE =
  '"Satoshi Medium", "Satoshi-Regular", sans-serif';
const GLOBAL_AI_TUTORIAL_FONT_MEDIUM =
  '"Satoshi Medium", "Satoshi-Regular", sans-serif';
const GLOBAL_AI_TUTORIAL_FONT_BOLD =
  '"Satoshi Medium", "Satoshi-Regular", sans-serif';

const buildRectSnapshot = (rect) => ({
  left: rect.left,
  top: rect.top,
  width: rect.width,
  height: rect.height,
  right: rect.right,
  bottom: rect.bottom,
});

const mergeElementRects = (elements) => {
  const rects = elements
    .filter(Boolean)
    .map((element) => element.getBoundingClientRect())
    .filter((rect) => rect.width > 0 && rect.height > 0);

  if (!rects.length) {
    return null;
  }

  const left = Math.min(...rects.map((rect) => rect.left));
  const top = Math.min(...rects.map((rect) => rect.top));
  const right = Math.max(...rects.map((rect) => rect.right));
  const bottom = Math.max(...rects.map((rect) => rect.bottom));

  return buildRectSnapshot({
    left,
    top,
    right,
    bottom,
    width: right - left,
    height: bottom - top,
  });
};

const buildGlobalAITutorialSteps = (t) => [
  {
    id: 'welcome',
    spotlight: null,
    title: t('eventsExplorerAI.tutorialWelcomeTitle'),
    body: t('eventsExplorerAI.tutorialWelcomeBody'),
    position: 'center',
  },
  {
    id: 'header',
    spotlight: 'header',
    title: t('eventsExplorerAI.tutorialHeaderTitle'),
    body: t('eventsExplorerAI.tutorialHeaderBody'),
    position: 'below',
  },
  {
    id: 'voice',
    spotlight: 'voice',
    title: t('eventsExplorerAI.tutorialVoiceTitle'),
    body: t('eventsExplorerAI.tutorialVoiceBody'),
    position: 'below',
  },
  {
    id: 'messages',
    spotlight: 'messages',
    title: t('eventsExplorerAI.tutorialMessagesTitle'),
    body: t('eventsExplorerAI.tutorialMessagesBody'),
    position: 'center',
  },
  {
    id: 'input',
    spotlight: 'input',
    title: t('eventsExplorerAI.tutorialInputTitle'),
    body: t('eventsExplorerAI.tutorialInputBody'),
    position: 'above',
  },
];

// --- SVG Icons ---

const SparklesSVG = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12,2 C12.3,2 12.5,2.2 12.6,2.5 L14.2,7.8 L19.5,9.4 C19.8,9.5 20,9.7 20,10 C20,10.3 19.8,10.5 19.5,10.6 L14.2,12.2 L12.6,17.5 C12.5,17.8 12.3,18 12,18 C11.7,18 11.5,17.8 11.4,17.5 L9.8,12.2 L4.5,10.6 C4.2,10.5 4,10.3 4,10 C4,9.7 4.2,9.5 4.5,9.4 L9.8,7.8 L11.4,2.5 C11.5,2.2 11.7,2 12,2 Z" />
    <path d="M19,2 C19.2,2 19.3,2.1 19.4,2.3 L20,4 L21.7,4.6 C21.9,4.7 22,4.8 22,5 C22,5.2 21.9,5.3 21.7,5.4 L20,6 L19.4,7.7 C19.3,7.9 19.2,8 19,8 C18.8,8 18.7,7.9 18.6,7.7 L18,6 L16.3,5.4 C16.1,5.3 16,5.2 16,5 C16,4.8 16.1,4.7 16.3,4.6 L18,4 L18.6,2.3 C18.7,2.1 18.8,2 19,2 Z" />
    <path d="M20,16 C20.2,16 20.3,16.1 20.4,16.3 L21,18 L22.7,18.6 C22.9,18.7 23,18.8 23,19 C23,19.2 22.9,19.3 22.7,19.4 L21,20 L20.4,21.7 C20.3,21.9 20.2,22 20,22 C19.8,22 19.7,21.9 19.6,21.7 L19,20 L17.3,19.4 C17.1,19.3 17,19.2 17,19 C17,18.8 17.1,18.7 17.3,18.6 L19,18 L19.6,16.3 C19.7,16.1 19.8,16 20,16 Z" />
  </svg>
);

// --- Message Renderer ---

const GlobalMessage = ({ message, currentStatus }) => {
  const { role, content, isLoading, isTranscribing, error } = message;
  const isUser = role === 'user';

  if (isTranscribing) {
    return (
      <MessageBubbleContainer $isUser={true}>
        <MessageBubble $isUser={true}>
          <MessageContent $isUser={true}>{content}</MessageContent>
        </MessageBubble>
      </MessageBubbleContainer>
    );
  }

  if (isLoading) {
    return (
      <MessageBubbleContainer $isUser={false}>
        <MessageBubble $isUser={false}>
          <LoadingDots><Dot /><Dot /><Dot /></LoadingDots>
          {currentStatus && <StatusText><span>⚡</span>{currentStatus}</StatusText>}
        </MessageBubble>
      </MessageBubbleContainer>
    );
  }

  return (
    <MessageBubbleContainer $isUser={isUser}>
      <MessageBubble $isUser={isUser}>
        <MessageContent $isUser={isUser}>
          {isUser ? content : <MarkdownRenderer>{content}</MarkdownRenderer>}
        </MessageContent>
        {error && <ErrorMsg>{typeof error === 'string' ? error : 'An error occurred'}</ErrorMsg>}
      </MessageBubble>
    </MessageBubbleContainer>
  );
};

// --- Main Panel ---

const EXAMPLE_PROMPTS = [
  "Find Arsenal and show their events",
  "Search for Bukayo Saka",
  "Show me Real Madrid's matches",
  "Find Messi's statistics",
];

const GlobalAIPanel = ({
  isOpen,
  onToggle,
  messages,
  isProcessing,
  currentStatus,
  onSendMessage,
  onResetChat,
  isRecording,
  voiceEnabled,
  onToggleVoice,
  voiceLanguage,
  onLanguageChange,
}) => {
  const { t } = useTranslation();
  const panelRef = useRef(null);
  const headerRef = useRef(null);
  const headerActionsRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const tutorialLaunchResolvedRef = useRef(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [tutorialRect, setTutorialRect] = useState(null);
  const tutorialSteps = useMemo(() => buildGlobalAITutorialSteps(t), [t]);
  const tutorialCurrentStep = showTutorial ? tutorialSteps[tutorialStep] : null;
  const tutorialIsLastStep = tutorialStep >= tutorialSteps.length - 1;

  const [showSettings, setShowSettings] = useState(false);
  const [languageFilter, setLanguageFilter] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [messages]);

  const startTutorial = useCallback(() => {
    setTutorialStep(0);
    setTutorialRect(null);
    setShowTutorial(true);
  }, []);

  const completeTutorial = useCallback(() => {
    try {
      localStorage.setItem(GLOBAL_AI_TUTORIAL_STORAGE_KEY, '1');
    } catch {
      // noop
    }
    setShowTutorial(false);
    setTutorialRect(null);
  }, []);

  const handleTutorialSkip = useCallback(() => {
    completeTutorial();
  }, [completeTutorial]);

  const handleTutorialNext = useCallback(() => {
    if (tutorialStep >= tutorialSteps.length - 1) {
      completeTutorial();
      return;
    }

    setTutorialStep((previousStep) => previousStep + 1);
  }, [completeTutorial, tutorialStep, tutorialSteps.length]);

  const handleTutorialReplay = useCallback(() => {
    startTutorial();
  }, [startTutorial]);

  const handlePanelToggle = useCallback(() => {
    if (isOpen) {
      setShowTutorial(false);
      setTutorialRect(null);
    }
    onToggle();
  }, [isOpen, onToggle]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (tutorialLaunchResolvedRef.current) {
      return;
    }

    tutorialLaunchResolvedRef.current = true;

    try {
      if (localStorage.getItem(GLOBAL_AI_TUTORIAL_STORAGE_KEY) !== '1') {
        startTutorial();
      }
    } catch {
      startTutorial();
    }
  }, [isOpen, startTutorial]);

  useEffect(() => {
    if (!showTutorial) {
      return undefined;
    }

    const currentStep = tutorialSteps[tutorialStep];

    if (!currentStep?.spotlight) {
      setTutorialRect(null);
      return undefined;
    }

    const updateRect = () => {
      const panelElement = panelRef.current;
      if (!panelElement) {
        setTutorialRect(null);
        return;
      }

      let nextRect = null;

      switch (currentStep.spotlight) {
        case 'header':
          nextRect = mergeElementRects([headerRef.current, headerActionsRef.current]);
          break;
        case 'voice':
          nextRect = mergeElementRects([headerActionsRef.current]);
          break;
        case 'messages':
          nextRect = mergeElementRects([messagesContainerRef.current]);
          break;
        case 'input':
          nextRect = mergeElementRects([inputContainerRef.current]);
          break;
        default:
          nextRect = null;
      }

      if (nextRect) {
        const panelRect = panelElement.getBoundingClientRect();
        nextRect = buildRectSnapshot({
          left: nextRect.left - panelRect.left,
          top: nextRect.top - panelRect.top,
          right: nextRect.right - panelRect.left,
          bottom: nextRect.bottom - panelRect.top,
          width: nextRect.width,
          height: nextRect.height,
        });
      }

      setTutorialRect(nextRect);
    };

    const timeoutId = window.setTimeout(updateRect, 70);
    const animationFrameId =
      typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame(updateRect)
        : null;

    const messageContainer = messagesContainerRef.current;

    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    if (messageContainer) {
      messageContainer.addEventListener('scroll', updateRect);
    }

    return () => {
      window.clearTimeout(timeoutId);
      if (
        animationFrameId !== null &&
        typeof window.cancelAnimationFrame === 'function'
      ) {
        window.cancelAnimationFrame(animationFrameId);
      }
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
      if (messageContainer) {
        messageContainer.removeEventListener('scroll', updateRect);
      }
    };
  }, [
    isOpen,
    isProcessing,
    messages.length,
    showTutorial,
    tutorialStep,
    tutorialSteps,
  ]);

  const tutorialSpotlightPadding = 10;
  const tutorialSpotlightStyle = tutorialRect
    ? {
        left: tutorialRect.left - tutorialSpotlightPadding,
        top: tutorialRect.top - tutorialSpotlightPadding,
        width: tutorialRect.width + tutorialSpotlightPadding * 2,
        height: tutorialRect.height + tutorialSpotlightPadding * 2,
      }
    : null;

  const tutorialCardStyle = (() => {
    const cardWidth = 340;
    const tutorialCardLeftOffset = 60;
    const panelWidth = panelRef.current?.clientWidth || cardWidth + 40;
    const panelHeight =
      panelRef.current?.clientHeight ||
      (typeof window !== 'undefined' ? window.innerHeight : 700);

    if (!tutorialCurrentStep) {
      return {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: cardWidth,
      };
    }

    if (tutorialCurrentStep.id === 'welcome') {
      if (panelWidth <= 420) {
        return {
          position: 'absolute',
          left: 16,
          right: 16,
          bottom: 16,
          top: 'auto',
          transform: 'none',
          width: 'auto',
        };
      }

      return {
        position: 'absolute',
        right: 16,
        bottom: 16,
        width: Math.min(cardWidth, panelWidth - 32),
        left: 'auto',
        top: 'auto',
        transform: 'none',
      };
    }

    if (!tutorialRect || tutorialCurrentStep.position === 'center') {
      return {
        position: 'absolute',
        top: '50%',
        left: `calc(50% - ${tutorialCardLeftOffset}px)`,
        transform: 'translate(-50%, -50%)',
        width: Math.min(cardWidth, panelWidth - 32),
      };
    }

    let top = 16;
    let left =
      tutorialRect.left +
      tutorialRect.width / 2 -
      Math.min(cardWidth, panelWidth - 32) / 2;
    const resolvedCardWidth = Math.min(cardWidth, panelWidth - 32);

    if (tutorialCurrentStep.position === 'below') {
      top = tutorialRect.bottom + tutorialSpotlightPadding + 18;
    } else if (tutorialCurrentStep.position === 'above') {
      top = tutorialRect.top - tutorialSpotlightPadding - 20 - 180;
    }

    left -= tutorialCardLeftOffset;
    left = Math.max(16, Math.min(left, panelWidth - resolvedCardWidth - 16));
    top = Math.max(16, Math.min(top, panelHeight - 220));

    return {
      position: 'absolute',
      top,
      left,
      width: resolvedCardWidth,
    };
  })();

  return (
    <>
      {!isOpen && (
        <ToggleButton onClick={onToggle} title="Open AI Search (hold Ctrl to use voice)" $isRecording={isRecording}>
          <SparklesIcon><SparklesSVG /></SparklesIcon>
        </ToggleButton>
      )}

      <PanelContainer ref={panelRef} $isOpen={isOpen}>
        <Header ref={headerRef} data-testid="global-ai-header">
          <HeaderTitle>
            <HeaderIcon><SparklesSVG /></HeaderIcon>
            <Title>AI Search</Title>
          </HeaderTitle>
          {!showTutorial && (
            <HeaderTutorialButton
              data-testid="global-ai-tutorial-help"
              type="button"
              onClick={handleTutorialReplay}
            >
              {t('eventsExplorerAI.tutorialHelp')}
            </HeaderTutorialButton>
          )}
          <HeaderActions ref={headerActionsRef} data-testid="global-ai-header-actions">
            <SettingsButton
              data-testid="global-ai-settings"
              onClick={() => { setShowSettings(prev => !prev); setLanguageFilter(''); }}
              title="Voice settings"
            >
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 0 0-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.49.49 0 0 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2z"/>
              </svg>
            </SettingsButton>
            {messages.length > 0 && (
              <ResetButton onClick={onResetChat} title="New Conversation">
                New Chat
              </ResetButton>
            )}
            <CloseButton onClick={handlePanelToggle} title="Close">
              &times;
            </CloseButton>
          </HeaderActions>
        </Header>

        <SettingsPanel $open={showSettings}>
          <SettingsPanelInner>
            <SettingsRow>
              <SettingsLabel>Voice mode</SettingsLabel>
              <SwitchLabel>
                <input
                  type="checkbox"
                  checked={voiceEnabled}
                  onChange={onToggleVoice}
                  data-testid="global-ai-voice-toggle"
                />
                <SwitchSlider />
              </SwitchLabel>
            </SettingsRow>
            <SettingsRow>
              <SettingsLabel>Language</SettingsLabel>
              <LanguageCode>{VOICE_LANGUAGES.find(l => l.code === voiceLanguage)?.label || 'Auto-detect'}</LanguageCode>
            </SettingsRow>
            <LanguageFilterInput
              type="text"
              placeholder="Filter languages..."
              value={languageFilter}
              onChange={(e) => setLanguageFilter(e.target.value)}
            />
            <LanguageList>
              {VOICE_LANGUAGES
                .filter(lang => {
                  if (!languageFilter) return true;
                  const q = languageFilter.toLowerCase();
                  return lang.label.toLowerCase().includes(q) || lang.code.toLowerCase().includes(q);
                })
                .map(lang => (
                  <LanguageItem
                    key={lang.code}
                    $selected={lang.code === voiceLanguage}
                    onClick={() => onLanguageChange(lang.code)}
                  >
                    {lang.label}
                    <LanguageCode>{lang.code}</LanguageCode>
                  </LanguageItem>
                ))
              }
            </LanguageList>
          </SettingsPanelInner>
        </SettingsPanel>

        <MessagesContainer ref={messagesContainerRef} data-testid="global-ai-messages">
          {messages.length === 0 ? (
            <WelcomeMessage>
              <WelcomeTitle>Hi! I'm your search assistant.</WelcomeTitle>
              <div>
                I can help you find teams and players and navigate to their pages.
                Try voice (hold Ctrl) or type a query:
              </div>
              <ExamplePrompts>
                {EXAMPLE_PROMPTS.map((prompt, index) => (
                  <ExamplePrompt key={index} onClick={() => onSendMessage(prompt)}>
                    "{prompt}"
                  </ExamplePrompt>
                ))}
              </ExamplePrompts>
            </WelcomeMessage>
          ) : (
            <>
              {messages.map((message, index) => (
                <GlobalMessage key={index} message={message} currentStatus={currentStatus} />
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </MessagesContainer>

        <InputContainer
          ref={inputContainerRef}
          data-testid="global-ai-input"
          $isRecording={isRecording}
        >
          {isRecording ? (
            <RecordingIndicator>Listening... (release Ctrl to send)</RecordingIndicator>
          ) : (
            <ChatInput onSend={onSendMessage} disabled={isProcessing} />
          )}
        </InputContainer>
        {showTutorial && tutorialCurrentStep && (
          <div
            data-testid="global-ai-tutorial-overlay"
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 1002,
              pointerEvents: 'none',
            }}
          >
            {tutorialCurrentStep.spotlight && tutorialRect ? (
              <svg
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'auto',
                }}
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <mask id="global-ai-tutorial-spotlight-mask">
                    <rect width="100%" height="100%" fill="white" />
                    <rect
                      x={tutorialRect.left - tutorialSpotlightPadding}
                      y={tutorialRect.top - tutorialSpotlightPadding}
                      width={tutorialRect.width + tutorialSpotlightPadding * 2}
                      height={tutorialRect.height + tutorialSpotlightPadding * 2}
                      rx="12"
                      fill="black"
                    />
                  </mask>
                </defs>
                <rect
                  width="100%"
                  height="100%"
                  fill="rgba(0,0,0,0.78)"
                  mask="url(#global-ai-tutorial-spotlight-mask)"
                />
              </svg>
            ) : (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(0,0,0,0.82)',
                  pointerEvents: 'auto',
                }}
              />
            )}

            {tutorialSpotlightStyle && (
              <div
                data-testid="global-ai-tutorial-spotlight"
                style={{
                  position: 'absolute',
                  ...tutorialSpotlightStyle,
                  borderRadius: 14,
                  border: `2px solid ${GLOBAL_AI_TUTORIAL_ACCENT}`,
                  boxShadow: `0 0 0 1px ${GLOBAL_AI_TUTORIAL_ACCENT}44, 0 0 24px ${GLOBAL_AI_TUTORIAL_ACCENT}66, 0 0 48px ${GLOBAL_AI_TUTORIAL_ACCENT}22`,
                  pointerEvents: 'none',
                  animation: 'globalAITutorialPulse 2s ease-in-out infinite',
                }}
              />
            )}

            <div
              data-testid="global-ai-tutorial-card"
              key={tutorialCurrentStep.id}
              style={{
                ...tutorialCardStyle,
                zIndex: 1005,
                background: GLOBAL_AI_TUTORIAL_CARD_BG,
                border: _isLightTheme
                  ? '1px solid #E5E7EB'
                  : `1px solid ${GLOBAL_AI_TUTORIAL_ACCENT}33`,
                borderRadius: 20,
                padding: '28px 28px 22px',
                boxShadow: _isLightTheme
                  ? '0 8px 32px rgba(0,0,0,0.12)'
                  : `0 24px 64px rgba(0,0,0,0.7), 0 0 0 1px ${GLOBAL_AI_TUTORIAL_ACCENT}22, inset 0 1px 0 ${GLOBAL_AI_TUTORIAL_ACCENT}11`,
                fontFamily: GLOBAL_AI_TUTORIAL_FONT_BASE,
                pointerEvents: 'auto',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 14,
                }}
              >
                {tutorialSteps.map((stepItem, index) => (
                  <div
                    key={stepItem.id}
                    style={{
                      height: 3,
                      flex: 1,
                      borderRadius: 99,
                      background:
                        index <= tutorialStep
                          ? GLOBAL_AI_TUTORIAL_ACCENT
                          : GLOBAL_AI_TUTORIAL_STEP_INACTIVE,
                      transition: 'background 0.4s',
                    }}
                  />
                ))}
              </div>

              <h2
                style={{
                  margin: '0 0 10px',
                  fontSize: tutorialCurrentStep.id === 'welcome' ? 22 : 18,
                  fontWeight: 800,
                  fontFamily: GLOBAL_AI_TUTORIAL_FONT_BOLD,
                  color: GLOBAL_AI_TUTORIAL_TITLE_COLOR,
                  lineHeight: 1.25,
                  letterSpacing: -0.3,
                }}
              >
                {tutorialCurrentStep.title}
              </h2>

              <p
                style={{
                  margin: '0 0 24px',
                  fontSize: 13.5,
                  fontFamily: GLOBAL_AI_TUTORIAL_FONT_BASE,
                  color: GLOBAL_AI_TUTORIAL_BODY_COLOR,
                  lineHeight: 1.6,
                }}
              >
                {tutorialCurrentStep.body}
              </p>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                }}
              >
                <button
                  type="button"
                  onClick={handleTutorialSkip}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: GLOBAL_AI_TUTORIAL_SKIP_COLOR,
                    fontSize: 13,
                    cursor: 'pointer',
                    padding: '6px 0',
                    fontFamily: GLOBAL_AI_TUTORIAL_FONT_MEDIUM,
                    fontWeight: 500,
                    textDecoration: 'underline',
                    textUnderlineOffset: 3,
                  }}
                >
                  {t('eventsExplorerAI.tutorialSkip')}
                </button>

                <button
                  type="button"
                  onClick={handleTutorialNext}
                  style={{
                    background: GLOBAL_AI_TUTORIAL_ACCENT,
                    border: 'none',
                    color: GLOBAL_AI_TUTORIAL_BTN_TEXT,
                    fontFamily: GLOBAL_AI_TUTORIAL_FONT_BOLD,
                    fontWeight: 800,
                    fontSize: 13.5,
                    padding: '10px 24px',
                    borderRadius: 10,
                    cursor: 'pointer',
                    letterSpacing: 0.3,
                    boxShadow: `0 4px 20px ${GLOBAL_AI_TUTORIAL_ACCENT}55`,
                  }}
                >
                  {tutorialIsLastStep
                    ? t('eventsExplorerAI.tutorialDone')
                    : t('eventsExplorerAI.tutorialNext')}
                </button>
              </div>
            </div>

            <style>
              {`
                @keyframes globalAITutorialPulse {
                  0%,100% { box-shadow: 0 0 0 1px ${GLOBAL_AI_TUTORIAL_ACCENT}44, 0 0 20px ${GLOBAL_AI_TUTORIAL_ACCENT}55, 0 0 40px ${GLOBAL_AI_TUTORIAL_ACCENT}22; }
                  50% { box-shadow: 0 0 0 2px ${GLOBAL_AI_TUTORIAL_ACCENT}88, 0 0 32px ${GLOBAL_AI_TUTORIAL_ACCENT}99, 0 0 64px ${GLOBAL_AI_TUTORIAL_ACCENT}44; }
                }
              `}
            </style>
          </div>
        )}
      </PanelContainer>
    </>
  );
};

export default GlobalAIPanel;
